import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Download, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { supabase } from '@/lib/supabase/client'

interface ImportJobRow {
  id: string
  file_name: string
  status: 'completed' | 'partial' | 'failed'
  commit_status: 'draft' | 'submitted'
  row_count: number
  records_created: number
  records_matched: number
  submissions_created: number
  submissions_updated: number
  failed_count: number
  created_at: string
}

async function fetchRecentJobs(projectId: string): Promise<ImportJobRow[]> {
  const { data, error } = await supabase
    .from('import_jobs')
    .select('id, file_name, status, commit_status, row_count, records_created, records_matched, submissions_created, submissions_updated, failed_count, created_at')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false })
    .limit(10)
  if (error) throw error
  return (data ?? []) as ImportJobRow[]
}

const STATUS_META = {
  completed: { icon: CheckCircle2, cls: 'text-emerald-500', label: 'موفق' },
  partial: { icon: AlertTriangle, cls: 'text-yellow-500', label: 'ناقص' },
  failed: { icon: XCircle, cls: 'text-destructive', label: 'ناموفق' },
}

export function DataIoPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()

  const { data: jobs = [] } = useQuery({
    queryKey: ['import-jobs', projectId],
    queryFn: () => fetchRecentJobs(projectId!),
    enabled: !!projectId,
  })

  return (
    <div className="flex flex-col h-full">
      <PageHeader title="ورود و خروجی داده" breadcrumbs={[{ label: 'ورود و خروجی داده' }]} />

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Action cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => navigate(`/projects/${projectId}/data-io/export`)}
            className="text-start rounded-2xl border border-border bg-card hover:border-primary/50 hover:bg-muted/20 transition-colors p-6 flex items-start gap-4"
          >
            <span className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <Download className="w-6 h-6" />
            </span>
            <div>
              <h3 className="font-semibold text-foreground">خروجی</h3>
              <p className="text-sm text-muted-foreground mt-1">
                داده و ساختار یک فرم، یک گروه یا کل پروژه — به CSV/Excel و JSON، یک‌جا در قالب ZIP.
              </p>
            </div>
          </button>

          <button
            onClick={() => navigate(`/projects/${projectId}/data-io/import`)}
            className="text-start rounded-2xl border border-border bg-card hover:border-primary/50 hover:bg-muted/20 transition-colors p-6 flex items-start gap-4"
          >
            <span className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <Upload className="w-6 h-6" />
            </span>
            <div>
              <h3 className="font-semibold text-foreground">ورود داده</h3>
              <p className="text-sm text-muted-foreground mt-1">
                از فایل CSV/Excel با نگاشت ستون‌ها، اعتبارسنجی و اصلاح، رکورد و فرم بسازید.
              </p>
            </div>
          </button>
        </div>

        {/* Recent imports */}
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4 text-muted-foreground" />
            واردسازی‌های اخیر
          </h3>
          {jobs.length === 0 ? (
            <p className="text-sm text-muted-foreground">هنوز واردسازی‌ای انجام نشده.</p>
          ) : (
            <div className="rounded-xl border border-border divide-y divide-border overflow-hidden">
              {jobs.map((j) => {
                const m = STATUS_META[j.status]
                const Icon = m.icon
                return (
                  <div key={j.id} className="flex items-center gap-3 px-4 py-3 text-sm">
                    <Icon className={`w-4 h-4 shrink-0 ${m.cls}`} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{j.file_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(j.created_at).toLocaleString('fa-IR')} ·{' '}
                        {j.commit_status === 'submitted' ? 'ثبت‌شده' : 'پیش‌نویس'}
                      </p>
                    </div>
                    <div className="text-xs text-muted-foreground text-end shrink-0">
                      <span className="text-emerald-500">{j.records_created} رکورد جدید</span>
                      {j.records_matched > 0 && <span> · {j.records_matched} تطبیق</span>}
                      {j.failed_count > 0 && <span className="text-destructive"> · {j.failed_count} ناموفق</span>}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
